import React, { useEffect, useRef, useState } from 'react'

import axios from 'axios'

import LoadingSpinner from '@src/components/icons/LoadingSpinner'

// Define the candle data structure from Jupiter API
interface CandleData {
    time: number
    open: number
    high: number
    low: number
    close: number
    volume: number
}

interface TokenSparklineProps {
    tokenMint: string
    width?: number
    height?: number
    color?: string
    decimals?: number
    usdPrice?: number
    defaultPeriod?: '24h' | '7d'
}

export const TokenSparkline: React.FC<TokenSparklineProps> = ({
    tokenMint,
    width = 0, // Will be determined by container if 0
    height = 35, // Reduced height for smaller screens
    color = '#4CAF50', // Default green color
    decimals = 9, // Default to 9 decimals (common for Solana tokens)
    usdPrice,
    defaultPeriod = '24h', // Default to 24 hours
}) => {
    // State for time period selection
    const [timePeriod, setTimePeriod] = useState<'24h' | '7d'>(defaultPeriod)
    // Use ref to get container dimensions
    const containerRef = useRef<HTMLDivElement>(null)
    const [containerWidth, setContainerWidth] = useState(width || 200) // Increased default width

    // Update container width on mount and resize
    useEffect(() => {
        const updateDimensions = () => {
            if (containerRef.current && width === 0) {
                // Get the full width of the container
                const fullWidth = containerRef.current.clientWidth
                // Ensure we have at least a minimum width for better visualization
                setContainerWidth(Math.max(fullWidth, 200))
            }
        }

        // Initial update
        updateDimensions()

        // Add resize listener
        window.addEventListener('resize', updateDimensions)

        // Cleanup
        return () => window.removeEventListener('resize', updateDimensions)
    }, [width])

    // Set loading state when time period changes
    useEffect(() => {
        setLoading(true)
    }, [timePeriod])
    // All React Hooks must be called at the top level
    const [priceData, setPriceData] = useState<CandleData[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [hoverIndex, setHoverIndex] = useState<number | null>(null)
    const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 })

    // Generate some sample data for testing when real data isn't available
    const generateSampleData = (count: number, trend: 'up' | 'down' | 'flat' = 'up'): CandleData[] => {
        const now = Date.now()
        // Use appropriate intervals and time ranges based on time period
        let interval: number
        let startTime: number

        if (timePeriod === '24h') {
            // Last 24 hours with 30-minute intervals
            interval = 30 * 60 * 1000 // 30 minutes in milliseconds
            startTime = now - 24 * 60 * 60 * 1000 // 24 hours ago
        } else {
            // Last 7 days with daily intervals
            interval = 24 * 60 * 60 * 1000 // 1 day in milliseconds
            startTime = now - 7 * 24 * 60 * 60 * 1000 // 7 days ago
        }

        // Generate more data points for 24h view to ensure smooth hover
        const actualCount = timePeriod === '24h' ? Math.max(count, 48) : count

        // Generate more realistic prices based on token type
        let basePrice: number
        if (tokenMint.toLowerCase().includes('usdt') || tokenMint.toLowerCase().includes('usdc')) {
            // Stablecoins should be close to $1
            basePrice = 0.98 + Math.random() * 0.04 // $0.98-$1.02 range
        } else if (usdPrice && !isNaN(usdPrice)) {
            // Use provided usdPrice as base if available
            basePrice = usdPrice * (0.95 + Math.random() * 0.1) // ±5-10% of provided price
        } else {
            // Random token price in a reasonable range
            const magnitude = Math.floor(Math.random() * 4) - 2 // -2 to 1 power of 10
            basePrice = (1 + Math.random() * 9) * Math.pow(10, magnitude) // $0.01 to $100 range
        }

        // Create an array of timestamps from startTime to now
        return Array.from({ length: actualCount }).map((_, i) => {
            let price: number
            const dayProgress = i / actualCount
            // Calculate the actual timestamp for this data point
            const time = startTime + i * interval

            if (trend === 'up') {
                // Upward trend with some randomness
                const trendFactor =
                    tokenMint.toLowerCase().includes('usdt') || tokenMint.toLowerCase().includes('usdc')
                        ? 0.02 // Stablecoins move very little
                        : 0.2 // Other tokens can move more
                price = basePrice * (1 + dayProgress * trendFactor + (Math.random() * 0.02 - 0.01))
            } else if (trend === 'down') {
                // Downward trend with some randomness
                const trendFactor =
                    tokenMint.toLowerCase().includes('usdt') || tokenMint.toLowerCase().includes('usdc')
                        ? 0.02 // Stablecoins move very little
                        : 0.2 // Other tokens can move more
                price = basePrice * (1 - dayProgress * trendFactor + (Math.random() * 0.02 - 0.01))
            } else {
                // Flat trend with small random movements
                const volatility =
                    tokenMint.toLowerCase().includes('usdt') || tokenMint.toLowerCase().includes('usdc')
                        ? 0.005 // Stablecoins have low volatility
                        : 0.04 // Other tokens have higher volatility
                price = basePrice * (1 + Math.random() * volatility * 2 - volatility)
            }

            return {
                time: time, // Use the calculated time based on startTime
                open: price * (1 - Math.random() * 0.01),
                high: price * (1 + Math.random() * 0.01),
                low: price * (1 - Math.random() * 0.01),
                close: price,
                volume: Math.random() * 1000000,
            }
        })
    }

    // State for current USD price (calculated or provided)
    const [currentUsdPrice, setCurrentUsdPrice] = useState<number | null>(null)

    useEffect(() => {
        const fetchPriceData = async () => {
            if (!tokenMint) {
                // Generate sample data even when no token mint is provided
                const sampleData = generateSampleData(96, 'flat')
                setPriceData(sampleData)
                setLoading(false)
                return
            }

            try {
                setLoading(true)
                // Try the Jupiter API endpoint with a short timeout
                // Use 1_DAY interval for longer period data (7 days)
                try {
                    // Calculate time ranges based on selected period
                    const endTime = Date.now() // Current time
                    let from: number
                    let interval: string

                    if (timePeriod === '24h') {
                        // Last 24 hours
                        from = endTime - 24 * 60 * 60 * 1000
                        interval = '15_MINUTE' // 15-minute intervals for 24h view (more granular)
                    } else {
                        // Last 7 days
                        from = endTime - 7 * 24 * 60 * 60 * 1000
                        interval = '1_HOUR' // Hourly intervals for 7d view (more data points)
                    }

                    // Use more data points for both time periods to ensure smooth lines
                    const candles = timePeriod === '24h' ? 96 : 168 // 96 candles for 24h (15 min intervals), 168 candles for 7d (hourly intervals)

                    // Check if this is a SOL token and use the correct address for Jupiter API
                    let mintAddress = tokenMint
                    // If the tokenMint is a SOL address in 0x format or some other incorrect format, use the correct Solana address
                    if (
                        tokenMint.toLowerCase() === 'sol' ||
                        (tokenMint.toLowerCase().startsWith('0x') && tokenMint.toLowerCase().includes('000000'))
                    ) {
                        mintAddress = 'So11111111111111111111111111111111111111112'
                    }

                    const url = `https://datapi.jup.ag/v2/charts/${mintAddress}?interval=${interval}&from=${from}&to=${endTime}&candles=${candles}`

                    const response = await axios.get(url, {
                        timeout: 5000, // Slightly longer timeout for more data
                        headers: {
                            Accept: 'application/json',
                            'Cache-Control': 'no-cache',
                        },
                    })

                    // Check if we have a valid response with candles data
                    if (response.data && Array.isArray(response.data.candles)) {
                        // If we have empty candles array, set empty data and show the "No price history" message
                        if (response.data.candles.length === 0) {
                            console.log(`TokenSparkline: Empty candles array for ${tokenMint}`)
                            setPriceData([])
                            setCurrentUsdPrice(usdPrice || null)
                            setError('No price history available')
                            setLoading(false)
                            return
                        }

                        // We have valid candles data, process it
                        // Check if the data needs normalization
                        const samplePrice = response.data.candles[0]?.close || 0
                        const needsNormalization = samplePrice > 1000000 // Heuristic: if price is very large, it might need normalization

                        // Ensure we're setting properly typed data and normalize only if needed
                        const typedCandles: CandleData[] = response.data.candles.map((candle: any) => {
                            let finalOpen = candle.open
                            let finalHigh = candle.high
                            let finalLow = candle.low
                            let finalClose = candle.close

                            // Only normalize if the price seems to need it
                            if (needsNormalization && decimals > 0) {
                                const divisor = Math.pow(10, decimals)
                                finalOpen = candle.open / divisor
                                finalHigh = candle.high / divisor
                                finalLow = candle.low / divisor
                                finalClose = candle.close / divisor
                            }

                            return {
                                time: candle.time,
                                open: finalOpen,
                                high: finalHigh,
                                low: finalLow,
                                close: finalClose,
                                volume: candle.volume,
                            }
                        })

                        // Set the current USD price (either from prop or calculate from last candle)
                        const lastCandle = typedCandles[typedCandles.length - 1]
                        setCurrentUsdPrice(usdPrice || lastCandle.close)

                        setPriceData(typedCandles)
                        setError(null)
                        setLoading(false)
                        return
                    } else {
                        throw new Error('Invalid response format')
                    }
                } catch (err) {
                    console.error(`TokenSparkline: API request failed for ${tokenMint}:`, err)
                    throw err // Propagate to outer catch
                }
            } catch (err) {
                // Check if this is a "No price history" error (empty candles)
                if (err instanceof Error && err.message === 'No price history available') {
                    // Don't use sample data for empty candles case
                    setPriceData([])
                    setCurrentUsdPrice(usdPrice || null)
                    setError(err.message)
                } else {
                    // For other errors, use sample data as fallback
                    console.log('Using sample data as fallback')
                    const trend = Math.random() > 0.6 ? 'up' : Math.random() > 0.3 ? 'down' : 'flat'
                    const sampleData = generateSampleData(96, trend)

                    // Set a sample USD price
                    const lastSample = sampleData[sampleData.length - 1]
                    setCurrentUsdPrice(usdPrice || lastSample.close)

                    setPriceData(sampleData)
                    setError(null)
                }
            } finally {
                setLoading(false)
            }
        }

        // Always attempt to fetch data, with a small delay to avoid blocking UI
        const timer = setTimeout(() => {
            fetchPriceData()
        }, 100)

        return () => clearTimeout(timer)
    }, [tokenMint, decimals, usdPrice, timePeriod])

    // Format USD price with appropriate precision
    const formatUsdPrice = (price: number | null): string => {
        if (price === null || price === 0) return '$0.00'

        // Special case for stablecoins to ensure they show close to $1.00
        if (tokenMint.toLowerCase().includes('usdt') || tokenMint.toLowerCase().includes('usdc')) {
            return `$${price.toFixed(2)}`
        }

        // Format based on price magnitude
        if (price >= 10000) return `$${price.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
        if (price >= 1000) return `$${price.toFixed(2)}`
        if (price >= 1) return `$${price.toFixed(4)}`
        if (price >= 0.01) return `$${price.toFixed(6)}`
        if (price > 0)
            return `$${price.toLocaleString('en-US', { minimumFractionDigits: 8, maximumFractionDigits: 8 })}`
        return '$0.00' // Fallback for any other case
    }

    // Show loading state
    if (loading) {
        return (
            <div className='relative flex w-full min-w-[200px] flex-col'>
                <div className='mb-1 flex items-center justify-between'>
                    <div className='flex items-center space-x-2'>
                        <button
                            className={`text-xs ${timePeriod === '24h' ? 'font-medium text-white' : 'text-mach-sub-text'}`}
                            onClick={() => setTimePeriod('24h')}
                        >
                            24h
                        </button>
                        <button
                            className={`text-xs ${timePeriod === '7d' ? 'font-medium text-white' : 'text-mach-sub-text'}`}
                            onClick={() => setTimePeriod('7d')}
                        >
                            7d
                        </button>
                    </div>
                    {/* Skeleton for price change percentage */}
                    <div className='h-4 w-12 animate-pulse rounded bg-mach-gray'></div>
                </div>

                {/* Skeleton for USD price */}
                <div className='mb-2 flex items-center justify-between'>
                    <div className='h-5 w-20 animate-pulse rounded bg-mach-gray'></div>
                </div>

                {/* Chart area with centered spinner */}
                <div className='relative mb-[10px] h-[10px] w-full'>
                    <div className='absolute inset-0 flex items-center justify-center rounded bg-mach-gray bg-opacity-50'>
                        <LoadingSpinner className='h-6 w-6 animate-spin text-white' stroke='white' />
                    </div>
                </div>
            </div>
        )
    }

    // Show a better UI for error or empty state
    if (error || priceData.length === 0) {
        return (
            <div className='relative flex w-full min-w-[200px] flex-col' ref={containerRef}>
                {/* Keep the time period selector for consistency */}
                <div className='mb-1 flex items-center justify-between'>
                    <div className='flex items-center space-x-2'>
                        <button
                            className={`text-xs ${timePeriod === '24h' ? 'font-medium text-white' : 'text-mach-sub-text'}`}
                            onClick={() => setTimePeriod('24h')}
                        >
                            24h
                        </button>
                        <button
                            className={`text-xs ${timePeriod === '7d' ? 'font-medium text-white' : 'text-mach-sub-text'}`}
                            onClick={() => setTimePeriod('7d')}
                        >
                            7d
                        </button>
                    </div>
                    <span className='text-xs font-medium text-mach-sub-text'>--</span>
                </div>

                {/* Show a placeholder for the price */}
                <div className='mb-2 flex items-center justify-between'>
                    <span className='text-sm font-medium text-white'>{formatUsdPrice(usdPrice || 0)}</span>
                </div>

                {/* Empty chart state with message */}
                <div className='flex h-[35px] w-full items-center justify-center rounded border border-mach-gray bg-mach-gray bg-opacity-20'>
                    <div className='text-xs text-mach-sub-text'>{error || 'No price history available'}</div>
                </div>
            </div>
        )
    }

    // Calculate points for the sparkline
    const getSparklinePoints = () => {
        if (priceData.length === 0) return ''

        // Get min and max values to normalize the data
        const values = priceData.map(candle => candle.close)
        const minValue = Math.min(...values)
        const maxValue = Math.max(...values)
        const range = maxValue - minValue

        // Use the actual width from state or props
        // For SVG with width='100%', we need to use viewBox to scale properly
        const chartWidth = 1000 // Use a fixed reference width for path coordinates

        // If there's no range, draw a flat line
        if (range === 0) {
            return `M 0,${height / 2} L ${chartWidth},${height / 2}`
        }

        // Calculate normalized points
        return priceData
            .map((candle, index) => {
                const x = (index / (priceData.length - 1)) * chartWidth
                // Invert the y-axis (SVG 0,0 is top-left)
                const y = height - ((candle.close - minValue) / range) * height
                return `${index === 0 ? 'M' : 'L'} ${x},${y}`
            })
            .join(' ')
    }

    // Calculate price change percentage
    const calculatePriceChange = () => {
        if (priceData.length < 2) return 0
        const firstPrice = priceData[0].close
        const lastPrice = priceData[priceData.length - 1].close
        return ((lastPrice - firstPrice) / firstPrice) * 100
    }

    const priceChange = calculatePriceChange()
    const sparklineColor = priceChange >= 0 ? color : '#FF5252' // Green if positive, red if negative

    // Handle mouse movement over the sparkline
    const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
        const svgRect = e.currentTarget.getBoundingClientRect()
        const x = e.clientX - svgRect.left
        const svgWidth = svgRect.width

        // Calculate x ratio based on the actual SVG width, not the prop width
        const xRatio = x / svgWidth

        // Calculate the data index based on the ratio and available data points
        const dataIndex = Math.min(Math.max(0, Math.floor(xRatio * priceData.length)), priceData.length - 1)

        setHoverIndex(dataIndex)
        setTooltipPosition({ x, y: e.clientY - svgRect.top })
    }

    const handleMouseLeave = () => {
        setHoverIndex(null)
    }

    // Format USD price with appropriate precision
    // Format price change since this data point
    const formatPriceChangeSince = (currentPrice: number, startPrice: number): string => {
        const percentChange = ((currentPrice - startPrice) / startPrice) * 100
        return `${percentChange >= 0 ? '+' : ''}${percentChange.toFixed(2)}%`
    }

    return (
        <div className='relative flex w-full min-w-[200px] flex-col' ref={containerRef}>
            <div className='mb-1 flex items-center justify-between'>
                <div className='flex items-center space-x-2'>
                    <button
                        className={`text-xs ${timePeriod === '24h' ? 'font-medium text-white' : 'text-mach-sub-text'}`}
                        onClick={() => {
                            if (timePeriod !== '24h') {
                                setTimePeriod('24h')
                                // Loading state is set by the useEffect
                            }
                        }}
                    >
                        24h
                    </button>
                    <button
                        className={`text-xs ${timePeriod === '7d' ? 'font-medium text-white' : 'text-mach-sub-text'}`}
                        onClick={() => {
                            if (timePeriod !== '7d') {
                                setTimePeriod('7d')
                                // Loading state is set by the useEffect
                            }
                        }}
                    >
                        7d
                    </button>
                </div>
                <span className={`text-xs font-medium ${priceChange >= 0 ? 'text-mach-green' : 'text-mach-red'}`}>
                    {priceChange.toFixed(2)}%
                </span>
            </div>

            {/* Current USD Price */}
            <div className='mb-2 flex items-center justify-between'>
                <span className='text-sm font-medium text-white'>{formatUsdPrice(currentUsdPrice)}</span>
            </div>

            <svg
                width='100%'
                height={height}
                className='w-full overflow-visible'
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
                preserveAspectRatio='none'
                viewBox={`0 0 1000 ${height}`}
                style={{ minWidth: '200px' }}
            >
                {/* Background for hover area */}
                <rect x='0' y='0' width='100%' height={height} fill='transparent' />
                {/* Main sparkline path */}
                <path
                    d={getSparklinePoints()}
                    fill='none'
                    stroke={sparklineColor}
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                />
                {/* Hover indicator */}
                {hoverIndex !== null && (
                    <>
                        {/* Vertical line at hover position */}
                        <line
                            x1={(hoverIndex / (priceData.length - 1)) * 1000}
                            y1='0'
                            x2={(hoverIndex / (priceData.length - 1)) * 1000}
                            y2={height}
                            stroke='rgba(255, 255, 255, 0.5)'
                            strokeWidth='1'
                            strokeDasharray='2,2'
                        />
                        {/* Dot at the data point */}
                        <circle
                            cx={(hoverIndex / (priceData.length - 1)) * 1000}
                            cy={
                                height -
                                ((priceData[hoverIndex].close - Math.min(...priceData.map(d => d.close))) /
                                    (Math.max(...priceData.map(d => d.close)) -
                                        Math.min(...priceData.map(d => d.close)))) *
                                    height
                            }
                            r='3'
                            fill={sparklineColor}
                            stroke='#000'
                            strokeWidth='1'
                        />
                    </>
                )}
            </svg>

            {/* Enhanced Tooltip */}
            {hoverIndex !== null && (
                <div
                    className='pointer-events-none absolute z-10 rounded bg-mach-gray px-2 py-1 text-xs text-white shadow-lg'
                    style={{
                        left: tooltipPosition.x < containerWidth / 2 ? tooltipPosition.x : tooltipPosition.x - 120,
                        top: -50,
                        minWidth: '120px',
                    }}
                >
                    <div className='font-medium'>Price: {formatUsdPrice(priceData[hoverIndex].close)}</div>
                    {/* Show percentage change from first data point */}
                    <div className='mt-1'>
                        Change:{' '}
                        <span
                            className={
                                priceData[hoverIndex].close > priceData[0].close ? 'text-mach-green' : 'text-mach-red'
                            }
                        >
                            {formatPriceChangeSince(priceData[hoverIndex].close, priceData[0].close)}
                        </span>
                    </div>
                </div>
            )}
        </div>
    )
}
